const CSS = `
:host { all: initial; }
.wrapper {
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-9999px, -9999px);
  opacity: 0;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
  will-change: transform;
}
.wrapper.visible { opacity: 1; }
.dot {
  width: 16px;
  height: 16px;
  margin-left: -8px;
  margin-top: -8px;
  border-radius: 50%;
  background: var(--artis-color, #111827);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35), 0 0 0 3px rgba(255, 255, 255, 0.6);
  transition: transform 0.15s ease;
}
.dot.clicking { transform: scale(0.55); }
.label {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--artis-color, #111827);
  color: #fff;
  font: 500 11px/1.6 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
`;

const MOVE_MS = 450;
const CLICK_PULSE_MS = 220;

/**
 * A visible "Agent" cursor that moves to whichever element a dom_action
 * targets before the action actually runs — so a visitor watching the page
 * sees the agent click/fill things the same way a real person would,
 * instead of the page just silently changing under them.
 *
 * Stays visible for the entire duration of a live call (shown as soon as the
 * call goes live, hidden only when the call ends) rather than fading out
 * between actions — a visitor should always be able to see the agent is
 * "there", not just catch a flash of it during a dom_action.
 *
 * Lives in its own shadow root (style-isolated from the host page, same
 * pattern as WidgetUI) and is `pointer-events: none` throughout, so it never
 * intercepts a real visitor's clicks.
 */
export class AgentCursor {
  private wrapper: HTMLDivElement;
  private dot: HTMLDivElement;
  private shown = false;

  constructor(label = "Agent") {
    const root = document.createElement("div");
    root.style.cssText =
      "position:fixed;inset:0;pointer-events:none;z-index:2147483646;";
    const shadow = root.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = CSS;
    shadow.appendChild(style);

    this.wrapper = document.createElement("div");
    this.wrapper.className = "wrapper";
    this.dot = document.createElement("div");
    this.dot.className = "dot";
    const labelEl = document.createElement("div");
    labelEl.className = "label";
    labelEl.textContent = label;
    this.wrapper.append(this.dot, labelEl);
    shadow.appendChild(this.wrapper);

    document.body.appendChild(root);
  }

  /** Shows the cursor at the center of the viewport — call as soon as the call goes live. */
  show(): void {
    if (this.shown) return;
    this.shown = true;
    this.wrapper.classList.add("visible");
    this.wrapper.style.transform = `translate(${window.innerWidth / 2}px, ${window.innerHeight / 2}px)`;
  }

  /** Hides the cursor for good — call when the live call ends. */
  hide(): void {
    this.shown = false;
    this.wrapper.classList.remove("visible");
  }

  /**
   * Animates the cursor to the target's current center. Does NOT scroll —
   * the caller (executeDomAction) already scrolls the element into view and
   * waits a frame before calling this. An earlier version scrolled again
   * here (a second, *smooth* scrollIntoView) and measured the rect after a
   * fixed 120ms guess-sleep: that smooth scroll frequently hadn't settled
   * yet at 120ms — more so on nested scrollable containers, which take
   * longer than a window-level scroll — so the cursor animated to a
   * mid-scroll, effectively random rect while the actual click (which
   * re-measures fresh, later) still landed correctly. Reading the rect
   * immediately, right after the caller's own scroll has already settled,
   * removes that race entirely.
   */
  async moveTo(el: Element): Promise<void> {
    this.show();
    const rect = el.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    this.wrapper.classList.add("visible");
    this.wrapper.style.transform = `translate(${x}px, ${y}px)`;
    await sleep(MOVE_MS);
  }

  /** Brief press-down animation — call right as the real click/fill happens. */
  async pulse(): Promise<void> {
    this.dot.classList.add("clicking");
    await sleep(CLICK_PULSE_MS);
    this.dot.classList.remove("clicking");
  }

}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let shared: AgentCursor | null = null;

/** Lazily-created singleton — one cursor per widget instance, reused across every dom_action. */
export function getAgentCursor(): AgentCursor {
  if (!shared) {
    shared = new AgentCursor();
  }
  return shared;
}
